import type {
  ApiResponse,
  ApiError,
  Project,
  Workflow,
  DashboardStats,
  Settings,
} from '@/types';

const API_BASE_URL = '/api/v1';

/**
 * Custom error class for API errors with typed error details
 */
export class ApiClientError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    public readonly details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

/**
 * Type-safe API client for the Mission Control backend
 */
class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * Generic fetch wrapper with error handling and type safety
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      let errorData: ApiError;
      try {
        errorData = await response.json();
      } catch {
        errorData = {
          code: 'UNKNOWN_ERROR',
          message: `Request failed with status ${response.status}`,
        };
      }
      throw new ApiClientError(
        errorData.code,
        errorData.message,
        response.status,
        errorData.details
      );
    }

    // Handle empty responses (204 No Content)
    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  // Dashboard endpoints
  async getDashboardStats(): Promise<DashboardStats> {
    const response = await this.request<ApiResponse<DashboardStats>>('/dashboard/stats');
    return response.data;
  }

  // Project endpoints
  async getProjects(params?: {
    page?: number;
    pageSize?: number;
    status?: string;
  }): Promise<ApiResponse<Project[]>> {
    const searchParams = new URLSearchParams();
    if (params?.page !== undefined) searchParams.set('page', String(params.page));
    if (params?.pageSize !== undefined) searchParams.set('pageSize', String(params.pageSize));
    if (params?.status) searchParams.set('status', params.status);

    const query = searchParams.toString();
    const endpoint = query ? `/projects?${query}` : '/projects';
    return this.request<ApiResponse<Project[]>>(endpoint);
  }

  async getProject(id: string): Promise<Project> {
    const response = await this.request<ApiResponse<Project>>(`/projects/${id}`);
    return response.data;
  }

  async createProject(data: Omit<Project, 'id' | 'createdAt' | 'updatedAt' | 'stats'>): Promise<Project> {
    const response = await this.request<ApiResponse<Project>>('/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  }

  async updateProject(id: string, data: Partial<Project>): Promise<Project> {
    const response = await this.request<ApiResponse<Project>>(`/projects/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    return response.data;
  }

  async deleteProject(id: string): Promise<void> {
    await this.request<void>(`/projects/${id}`, {
      method: 'DELETE',
    });
  }

  // Workflow endpoints
  async getWorkflows(params?: {
    page?: number;
    pageSize?: number;
    projectId?: string;
    status?: string;
  }): Promise<ApiResponse<Workflow[]>> {
    const searchParams = new URLSearchParams();
    if (params?.page !== undefined) searchParams.set('page', String(params.page));
    if (params?.pageSize !== undefined) searchParams.set('pageSize', String(params.pageSize));
    if (params?.projectId) searchParams.set('projectId', params.projectId);
    if (params?.status) searchParams.set('status', params.status);

    const query = searchParams.toString();
    const endpoint = query ? `/workflows?${query}` : '/workflows';
    return this.request<ApiResponse<Workflow[]>>(endpoint);
  }

  async getWorkflow(id: string): Promise<Workflow> {
    const response = await this.request<ApiResponse<Workflow>>(`/workflows/${id}`);
    return response.data;
  }

  async getProjectWorkflows(projectId: string): Promise<Workflow[]> {
    const response = await this.request<ApiResponse<Workflow[]>>(`/projects/${projectId}/workflows`);
    return response.data;
  }

  async triggerWorkflow(projectId: string, workflowName: string): Promise<Workflow> {
    const response = await this.request<ApiResponse<Workflow>>(`/projects/${projectId}/workflows/trigger`, {
      method: 'POST',
      body: JSON.stringify({ workflowName }),
    });
    return response.data;
  }

  async cancelWorkflow(id: string): Promise<Workflow> {
    const response = await this.request<ApiResponse<Workflow>>(`/workflows/${id}/cancel`, {
      method: 'POST',
    });
    return response.data;
  }

  // Settings endpoints
  async getSettings(): Promise<Settings> {
    const response = await this.request<ApiResponse<Settings>>('/settings');
    return response.data;
  }

  async updateSettings(data: Partial<Settings>): Promise<Settings> {
    const response = await this.request<ApiResponse<Settings>>('/settings', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    return response.data;
  }
}

// Export singleton instance
export const api = new ApiClient();

// Export class for testing
export { ApiClient };
